/**
 * Shared control styles for the reader sidebar panels.
 *
 * Every button, input, textarea, and field label in the Bookmarks / Notes /
 * Flashcards / Print / Download / Settings / Info panels should pull from
 * this module so the visual language stays consistent. When something needs
 * to change, update it *here* — not in each panel file.
 *
 * Panels that use inline `style` props import the CSS-property objects
 * (`controlStyles.*`). Panels that use Tailwind classes import the matching
 * class strings (`controlClasses.*`). Both produce the same visual.
 */

import type { CSSProperties } from 'react';

// ---------------------------------------------------------------------------
// Inline style objects
// ---------------------------------------------------------------------------

/** Primary filled button — use for Save / Create / primary confirm actions. */
export const primaryButtonStyle: CSSProperties = {
  padding: '6px 14px',
  fontSize: '0.813rem',
  fontWeight: 600,
  backgroundColor: 'rgb(var(--color-primary-600))',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'background-color 0.15s',
  whiteSpace: 'nowrap',
};

/** Ghost outlined button — use for Cancel / secondary actions. */
export const secondaryButtonStyle: CSSProperties = {
  padding: '6px 14px',
  fontSize: '0.813rem',
  fontWeight: 500,
  backgroundColor: 'transparent',
  color: 'rgb(var(--color-neutral-700))',
  border: '1px solid rgb(var(--color-neutral-300))',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'background-color 0.15s, border-color 0.15s',
  whiteSpace: 'nowrap',
};

/** Small square icon button — use for inline Edit / Delete icons on list items. */
export const iconButtonStyle: CSSProperties = {
  width: '28px',
  height: '28px',
  padding: '4px',
  background: 'none',
  border: 'none',
  color: 'var(--color-text-tertiary)',
  cursor: 'pointer',
  borderRadius: '4px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.15s, color 0.15s',
};

/** Compact text input — single line. */
export const compactInputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  fontSize: '0.813rem',
  lineHeight: 1.4,
  border: '1px solid rgb(var(--color-neutral-300))',
  borderRadius: '6px',
  background: 'var(--color-surface)',
  color: 'var(--color-text-primary)',
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

/** Compact textarea — multi-line. */
export const compactTextareaStyle: CSSProperties = {
  ...compactInputStyle,
  resize: 'vertical',
};

/** Small uppercase field label (sits above an input). */
export const fieldLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: '0.688rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  marginBottom: '4px',
};

/** Row layout for Save + Cancel buttons. Apply to the wrapping div. */
export const buttonRowStyle: CSSProperties = {
  marginTop: '8px',
  display: 'flex',
  gap: '8px',
};

// ---------------------------------------------------------------------------
// Tailwind class strings (for panels that use className instead of inline)
// These MUST be visually equivalent to the inline objects above.
// ---------------------------------------------------------------------------

export const controlClasses = {
  /** Primary filled button. */
  btnPrimary:
    'px-3.5 py-1.5 text-[13px] font-semibold bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap',

  /** Ghost outlined secondary button. */
  btnSecondary:
    'px-3.5 py-1.5 text-[13px] font-medium text-neutral-700 bg-transparent border border-neutral-300 rounded-md hover:bg-neutral-100 transition-colors whitespace-nowrap',

  /** Small square icon button. */
  btnIcon:
    'w-7 h-7 p-1 inline-flex items-center justify-center text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded transition-colors',

  /** Destructive icon button (delete). */
  btnIconDanger:
    'w-7 h-7 p-1 inline-flex items-center justify-center text-neutral-400 hover:text-error-600 hover:bg-error-50 rounded transition-colors',

  /** Compact input (single line). */
  input:
    'w-full px-2.5 py-2 text-[13px] leading-snug border border-neutral-300 rounded-md bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',

  /** Compact textarea. */
  textarea:
    'w-full px-2.5 py-2 text-[13px] leading-snug border border-neutral-300 rounded-md bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y',

  /** Small uppercase field label. */
  fieldLabel:
    'block text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-1',

  /** Panel outer wrapper (consistent padding). */
  panel: 'w-full p-4',
};
